import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, Bell, FileSpreadsheet } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

const sgd = (n) => `S$${Number(n || 0).toLocaleString("en-SG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const label = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-SG", { month: "short", year: "2-digit" });

const Tile = ({ k, v, testid }) => (
  <div className="rounded-lg border p-3"><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{k}</div><div className="text-xl font-semibold tabular-nums" data-testid={testid}>{v}</div></div>
);

export const SalesSection = ({ call }) => {
  const [d, setD] = useState(null);
  useEffect(() => { call("/sales").then(setD).catch((x) => toast.error(x.message)); }, [call]);
  if (!d) return null;
  const data = d.months.map((m) => ({ ...m, name: label(m.month) }));
  const exportCsv = () => {
    const head = ["Month", "Pro sales (count)", "Pro sales (SGD)", "QR orders (count)", "QR orders (SGD)", "Invoices paid (count)", "Invoices paid (SGD)", "Total (SGD)"];
    const rows = d.months.map((m) => [m.month, m.pro_count, m.pro_amount.toFixed(2), m.qr_count, m.qr_amount.toFixed(2), m.invoice_count, m.invoice_amount.toFixed(2), m.total.toFixed(2)]);
    rows.push(["TOTAL", "", d.months.reduce((a, m) => a + m.pro_amount, 0).toFixed(2), "", d.months.reduce((a, m) => a + m.qr_amount, 0).toFixed(2), "", d.months.reduce((a, m) => a + m.invoice_amount, 0).toFixed(2), d.total.toFixed(2)]);
    const csv = "\uFEFF" + [head, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" })), download: `paperlite-sales-${new Date().toISOString().slice(0, 10)}.csv` });
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <section className="lw-card p-5 space-y-4" data-testid="admin-sales">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4" />Sales by month</h2>
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={!d.months.length} data-testid="sales-export-btn"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Export spreadsheet (.csv)</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Tile k="Total received" v={sgd(d.total)} testid="sales-total" />
        <Tile k="Pro licences sold" v={d.pro_sales} testid="sales-pro-count" />
        <Tile k="Invoices paid" v={d.invoices_paid} testid="sales-invoice-count" />
      </div>
      {data.length ? (
        <>
          <div className="h-64 -ml-3" data-testid="sales-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} maxBarSize={56}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} width={56} />
                <Tooltip formatter={(v) => sgd(v)} cursor={{ fill: "rgba(148,163,184,0.15)" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="pro_amount" name="Pro (Stripe / PayPal)" stackId="a" fill="#0F6CBD" />
                <Bar dataKey="qr_amount" name="Pro (QR orders)" stackId="a" fill="#F59E0B" />
                <Bar dataKey="invoice_amount" name="Invoices" stackId="a" fill="#10B981" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sales-table">
              <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1.5">Month</th><th>Pro</th><th>QR orders</th><th>Invoices</th><th className="text-right">Total</th></tr></thead>
              <tbody className="divide-y">{[...data].reverse().map((m) => (
                <tr key={m.month} data-testid={`sales-row-${m.month}`}><td className="py-1.5">{m.name}</td><td>{m.pro_count} · {sgd(m.pro_amount)}</td><td>{m.qr_count} · {sgd(m.qr_amount)}</td><td>{m.invoice_count} · {sgd(m.invoice_amount)}</td><td className="text-right font-medium">{sgd(m.total)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </>
      ) : <p className="text-sm text-muted-foreground">No payments yet.</p>}
    </section>
  );
};

export const AlertSettings = ({ call }) => {
  const [email, setEmail] = useState("");
  useEffect(() => { call("/settings").then((s) => setEmail(s.owner_email || "")).catch(() => {}); }, [call]);
  const save = async (e) => {
    e.preventDefault();
    try { await call("/settings", "PUT", { owner_email: email }); toast.success(email ? "Payment alerts will go to " + email : "Payment alerts turned off"); }
    catch (x) { toast.error(x.message); }
  };
  return (
    <form onSubmit={save} className="lw-card p-5 space-y-2" data-testid="admin-alerts">
      <h2 className="font-semibold flex items-center gap-2"><Bell className="h-4 w-4" />Payment alerts</h2>
      <p className="text-xs text-muted-foreground">An email is sent here for every confirmed Pro sale and paid invoice (card, PayNow or PayPal checkout). Leave empty to turn alerts off.</p>
      <div className="flex gap-2">
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="flex-1" data-testid="admin-alert-email-input" />
        <Button type="submit" data-testid="admin-alert-save-btn">Save</Button>
      </div>
    </form>
  );
};
